import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Row, Col, Card, Spinner, Modal, Button, Alert } from 'react-bootstrap';
import { FiArrowLeft } from 'react-icons/fi';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import PersonalVaultUnlockAlert from './PersonalVaultUnlockAlert';
import CredentialVaultNavUnlockModal, {
  useCredentialVaultModalContentShift,
} from './CredentialVaultNavUnlockModal';
import {
  getCredentialById,
  revealCredential,
  deleteCredential,
  getCredentialActivities,
  recordCredentialClientAction,
} from '../../services/CredentialVaultService';
import { decryptCredential } from '../../crypto/credentialVaultCrypto';
import {
  buildCredentialVaultListLocation,
  credentialVaultListNavigateState,
  credentialVaultListReturnState,
  CredentialVaultGateShell,
  useCredentialVaultCrypto,
} from './VaultCryptoProvider';
import { usePageToolbar } from '../layouts/nav/PageToolbarContext';
import '../settings/commonstyle.css';
import '../users/userlist.css';
import '../tasks/tasklist.css';
import '../tasks/taskdetails.css';
import './credentialVault.css';

function summarizeAccess(row) {
  if (row.is_private) {
    const origin = String(row.credential_list_origin || 'personal').toLowerCase();
    if (origin === 'corporate')
      return 'Private to you (organization vault; not listed under Personal credentials)';
    return 'Private (only you)';
  }
  const u = (row.user_ids && row.user_ids.length) || 0;
  const sd = (row.sub_department_ids && row.sub_department_ids.length) || 0;
  const d = (row.department_ids && row.department_ids.length) || 0;
  const b = (row.branch_ids && row.branch_ids.length) || 0;
  if (u > 0) return `${u} user${u === 1 ? '' : 's'} only`;
  if (sd > 0) return `${sd} sub-dept${sd === 1 ? '' : 's'}`;
  if (d > 0) return `${d} dept.${d === 1 ? '' : 's'}`;
  if (b > 0) return `${b} branch${b === 1 ? '' : 'es'}`;
  return 'Whole organization';
}

function credentialVaultListPath(row) {
  if (!row?.is_private) return '/credential-vault/credentialVaultList';
  const origin = String(row.credential_list_origin || 'personal').toLowerCase();
  return origin === 'corporate' ? '/credential-vault/credentialVaultList' : '/credential-vault/personalVaultList';
}

function credentialVaultFormUiSide(row) {
  if (!row?.is_private) return 'corporate';
  const origin = String(row.credential_list_origin || 'personal').toLowerCase();
  return origin === 'corporate' ? 'corporate' : 'personal';
}

function formatMyAccessLevel(level) {
  if (level === 'full') return 'Full access';
  if (level === 'edit') return 'Edit (fields only)';
  if (level === 'view') return 'View only';
  return '—';
}

function toWebsiteHref(raw) {
  if (!raw || !String(raw).trim()) return null;
  const s = String(raw).trim();
  if (/^https?:\/\//i.test(s)) return s;
  return `https://${s}`;
}

function formatDetailTimestamp(value) {
  if (value == null || value === '') return '—';
  try {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString();
  } catch {
    return '—';
  }
}

/** Minimum time the password modal shows a loading state (UX + perceived security). */
const MIN_PASSWORD_MODAL_LOAD_MS = 1000;

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const ACTION_LABELS = {
  create: 'Created',
  view: 'Viewed details',
  update: 'Updated',
  password_revealed: 'Password revealed',
  password_copied: 'Password copied to clipboard',
  credential_details_copied: 'All Credential fields copied to clipboard',
};

function formatActivityAction(row) {
  const base = ACTION_LABELS[row.action] || row.action;
  const fields = row.meta?.changed_fields;
  if (row.action === 'update' && Array.isArray(fields) && fields.length > 0) {
    return `${base} (${fields.join(', ')})`;
  }
  return base;
}

function CredentialVaultDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    dek,
    personalDek,
    vaultInitialized,
    personalVaultInitialized,
    unlockPersonalVaultSession,
    vaultIdleTimeoutMinutes,
    touchCredentialVaultIdleFromActivity,
    touchPersonalVaultIdleFromActivity,
  } = useCredentialVaultCrypto();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [copyAllLoading, setCopyAllLoading] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordModalLoading, setPasswordModalLoading] = useState(false);
  const [passwordModalText, setPasswordModalText] = useState('');
  const [notesModalText, setNotesModalText] = useState('');
  const [passwordModalError, setPasswordModalError] = useState('');
  const [revealModalSurface, setRevealModalSurface] = useState('password');
  const passwordModalFlowLockRef = useRef(false);
  /** Disables eye/copy icons while a modal password flow is running. */
  const [passwordRowBusy, setPasswordRowBusy] = useState(false);
  const [personalUnlockOpen, setPersonalUnlockOpen] = useState(false);
  const [deleteUnlockOpen, setDeleteUnlockOpen] = useState(false);
  const [passwordAccessUnlockOpen, setPasswordAccessUnlockOpen] = useState(false);
  /** 'view_password' | 'view_notes' | 'copy' | 'copy_all' | 'copy_notes' — after vault unlock */
  const pendingSecretActionRef = useRef(null);
  const { shiftPx: passwordRevealShiftPx, remeasure: remeasurePasswordRevealShift } =
    useCredentialVaultModalContentShift(passwordModalOpen);
  const passwordRevealModalStyle = passwordModalOpen
    ? { '--cv-shift-x': `${passwordRevealShiftPx}px` }
    : undefined;
  const [personalUnlockPw, setPersonalUnlockPw] = useState('');
  const [showPersonalUnlockPw, setShowPersonalUnlockPw] = useState(false);
  const [personalUnlockBusy, setPersonalUnlockBusy] = useState(false);
  const [personalUnlockError, setPersonalUnlockError] = useState('');

  const notifyError = (msg) => toast.error(`❌ ${msg}`, { position: 'top-right' });

  /** Decrypt via reveal API; only password is merged into detail. Notes plaintext is never stored on the card — modal and clipboard only.
   * @param {{ revealParts?: 'password' | 'notes' | 'both' }} [options] — decrypt/fetch only the requested parts (default both). */
  const revealAndMergeSecrets = useCallback(
    async (dekOverrides, options = {}) => {
      const revealParts = options.revealParts ?? 'both';
      if (!id || !detail) return null;
      const needsPassword =
        (revealParts === 'both' || revealParts === 'password') &&
        (detail.password == null || detail.password === '');
      const needsNotes =
        (revealParts === 'both' || revealParts === 'notes') &&
        !!detail.has_notes &&
        (detail.notes == null || detail.notes === '');
      if (!needsPassword && !needsNotes) {
        return {
          password: detail.password != null ? String(detail.password) : '',
          notes: detail.notes != null ? String(detail.notes) : '',
        };
      }
      try {
        const res = await revealCredential(id);
        if (!res.data?.success) {
          notifyError('Could not load encrypted fields');
          return null;
        }
        const data = res.data.data;
        let password = detail.password;
        let notes = detail.notes;

        const encryptionMode = data.encryptionMode || data.encryption_mode;
        if (encryptionMode === 'client_aes_gcm') {
          const scope =
            data.encryptionKeyScope === 'personal'
              ? 'personal'
              : data.encryptionKeyScope === 'corporate'
                ? 'corporate'
                : detail?.private_uses_personal_dek
                  ? 'personal'
                  : 'corporate';
          const dekToUse =
            scope === 'personal'
              ? dekOverrides?.personalDek ?? personalDek
              : dekOverrides?.corporateDek ?? dek;
          if (!dekToUse) {
            if (scope === 'personal') {
              setPersonalUnlockOpen(true);
            } else {
              notifyError('Unlock the Credentials Vault to reveal secrets');
            }
            return null;
          }
          if (needsPassword) {
            password = await decryptCredential(
              { iv: data.password_iv, data: data.encrypted_password },
              dekToUse
            );
          }
          if (needsNotes) {
            if (data.encrypted_notes && data.notes_iv) {
              notes = await decryptCredential(
                { iv: data.notes_iv, data: data.encrypted_notes },
                dekToUse
              );
            } else if (data.notes != null && String(data.notes) !== '') {
              notes = data.notes;
            } else {
              notes = '';
            }
          }
        } else {
          if (needsPassword) {
            password = data.password;
          }
          if (needsNotes) {
            notes = data.notes != null && String(data.notes) !== '' ? data.notes : '';
          }
        }

        if (needsPassword && (password == null || password === '')) {
          notifyError('Password is empty');
          return null;
        }

        setDetail((prev) => {
          if (!prev) return prev;
          const next = { ...prev };
          if (needsPassword && password != null) next.password = password;
          return next;
        });

        return {
          password: password != null ? String(password) : '',
          notes: notes != null ? String(notes) : '',
        };
      } catch (e) {
        notifyError(e?.response?.data?.message || 'Could not load credential secrets');
        return null;
      }
    },
    [id, detail, dek, personalDek]
  );

  const resolvePlaintextPassword = useCallback(
    async (dekOverrides) => {
      const r = await revealAndMergeSecrets(dekOverrides, { revealParts: 'password' });
      if (!r) return null;
      if (!r.password) {
        notifyError('Nothing to copy');
        return null;
      }
      return r.password;
    },
    [revealAndMergeSecrets]
  );

  const buildCredentialClipboardText = useCallback(
    (passwordPlain, notesPlain) => {
      const lines = [
        `Title: ${detail?.title ?? '—'}`,
        `Website: ${detail?.website?.trim() ? detail.website : '—'}`,
        `Provider: ${detail?.provider_name || '—'}`,
        `User name: ${detail?.user_name ?? '—'}`,
        `Password: ${passwordPlain}`,
      ];
      if (notesPlain != null && String(notesPlain).trim() !== '') {
        lines.push(`Notes:\n${notesPlain}`);
      }
      return lines.join('\n');
    },
    [detail]
  );

  const fetchActivities = useCallback(async () => {
    if (!id) return;
    setActivitiesLoading(true);
    try {
      const res = await getCredentialActivities(id, { limit: 50, page: 1 });
      if (res.data?.success) {
        setActivities(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        setActivities([]);
      }
    } catch {
      setActivities([]);
    } finally {
      setActivitiesLoading(false);
    }
  }, [id]);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setActivitiesLoading(true);
    setLoadError('');
    setDetail(null);
    setActivities([]);
    try {
      const results = await Promise.allSettled([
        getCredentialById(id),
        getCredentialActivities(id, { limit: 50, page: 1 }),
      ]);
      const credRes = results[0].status === 'fulfilled' ? results[0].value : null;
      if (!credRes?.data?.success) {
        const err = results[0].status === 'rejected' ? results[0].reason : null;
        const msg =
          err?.response?.data?.message || credRes?.data?.message || 'Could not load Credential';
        setLoadError(msg);
        notifyError(msg);
        setActivities([]);
        return;
      }
      setDetail(credRes.data.data);

      const actRes = results[1].status === 'fulfilled' ? results[1].value : null;
      if (actRes?.data?.success && Array.isArray(actRes.data.data)) {
        setActivities(actRes.data.data);
      } else {
        setActivities([]);
      }
    } catch (e) {
      const msg = e?.response?.data?.message || 'Could not load Credential';
      setLoadError(msg);
      notifyError(msg);
      setActivities([]);
    } finally {
      setLoading(false);
      setActivitiesLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPasswordModalOpen(false);
    setPasswordModalLoading(false);
    setPasswordModalText('');
    setNotesModalText('');
    setPasswordModalError('');
    setRevealModalSurface('password');
    passwordModalFlowLockRef.current = false;
    setPasswordRowBusy(false);
    pendingSecretActionRef.current = null;
    setPasswordAccessUnlockOpen(false);
  }, [id]);

  useEffect(() => {
    touchCredentialVaultIdleFromActivity();
    touchPersonalVaultIdleFromActivity();
  }, [id, touchCredentialVaultIdleFromActivity, touchPersonalVaultIdleFromActivity]);

  const navigateBackToCredentialList = () => {
    if (!detail) return;
    const path = credentialVaultListPath(detail);
    const side = path.endsWith('/personalVaultList') ? 'personal' : 'corporate';
    const searchFromState =
      location.state?.credentialVaultListSearch != null &&
      String(location.state.credentialVaultListSearch).trim() !== ''
        ? { credentialVaultListSearch: String(location.state.credentialVaultListSearch) }
        : {};
    navigate({
      ...buildCredentialVaultListLocation(path, location.state),
      state: {
        ...credentialVaultListReturnState(side),
        ...searchFromState,
      },
    });
  };

  const goList = () => navigateBackToCredentialList();

  const confirmPersonalUnlockDetail = async () => {
    setPersonalUnlockError('');
    if (!personalUnlockPw.trim()) {
      setPersonalUnlockError('Personal vault password is required');
      return;
    }
    setPersonalUnlockBusy(true);
    try {
      await unlockPersonalVaultSession(personalUnlockPw);
      setPersonalUnlockPw('');
      setPersonalUnlockOpen(false);
      toast.success('Personal vault unlocked', { position: 'top-right', autoClose: 2500 });
    } catch (e) {
      setPersonalUnlockError(e?.response?.data?.message || e?.message || 'Incorrect password');
    } finally {
      setPersonalUnlockBusy(false);
    }
  };

  const closePasswordModal = () => {
    setPasswordModalOpen(false);
    setPasswordModalLoading(false);
    setPasswordModalText('');
    setNotesModalText('');
    setPasswordModalError('');
    setRevealModalSurface('password');
    passwordModalFlowLockRef.current = false;
    setPasswordRowBusy(false);
  };

  /** Reveal either password or notes in the modal (after vault unlock), never both at once. */
  const openPasswordModalFlow = async (dekOverrides, modalSurface /* 'password' | 'notes' */) => {
    if (!id || !detail || passwordModalFlowLockRef.current) return;
    const revealParts = modalSurface === 'notes' ? 'notes' : 'password';
    passwordModalFlowLockRef.current = true;
    setPasswordRowBusy(true);
    setRevealModalSurface(modalSurface);
    setPasswordModalOpen(true);
    setPasswordModalLoading(true);
    setPasswordModalText('');
    setNotesModalText('');
    setPasswordModalError('');
    const started = Date.now();
    try {
      const merged = await revealAndMergeSecrets(dekOverrides, { revealParts });
      const elapsed = Date.now() - started;
      if (elapsed < MIN_PASSWORD_MODAL_LOAD_MS) {
        await delay(MIN_PASSWORD_MODAL_LOAD_MS - elapsed);
      }
      if (merged == null) {
        setPasswordModalLoading(false);
        setPasswordModalOpen(false);
        return;
      }
      if (modalSurface === 'password') {
        const hasPwd = merged.password && String(merged.password).length > 0;
        if (!hasPwd) {
          notifyError('Nothing to show');
          setPasswordModalLoading(false);
          setPasswordModalOpen(false);
          return;
        }
        setPasswordModalText(merged.password);
        setNotesModalText('');
      } else {
        const hasNotesPlain =
          !!detail.has_notes &&
          merged.notes != null &&
          String(merged.notes).trim() !== '';
        if (!hasNotesPlain) {
          notifyError('No notes to show');
          setPasswordModalLoading(false);
          setPasswordModalOpen(false);
          return;
        }
        setPasswordModalText('');
        setNotesModalText(String(merged.notes));
      }
      setPasswordModalLoading(false);
      if (modalSurface === 'password') {
        try {
          await recordCredentialClientAction(id, { action: 'password_revealed' });
          void fetchActivities();
        } catch {
          /* audit is best-effort */
        }
      }
    } catch (e) {
      const elapsedCatch = Date.now() - started;
      if (elapsedCatch < MIN_PASSWORD_MODAL_LOAD_MS) {
        await delay(MIN_PASSWORD_MODAL_LOAD_MS - elapsedCatch);
      }
      setPasswordModalLoading(false);
      setPasswordModalError(
        e?.response?.data?.message ||
          e?.message ||
          (modalSurface === 'notes' ? 'Could not load notes' : 'Could not load password')
      );
    } finally {
      passwordModalFlowLockRef.current = false;
      setPasswordRowBusy(false);
    }
  };

  /** Copy password after vault unlock — no reveal modal. */
  const copyPasswordAfterUnlock = async (dekOverrides) => {
    if (!id || !detail || passwordModalFlowLockRef.current) return;
    passwordModalFlowLockRef.current = true;
    setPasswordRowBusy(true);
    const started = Date.now();
    try {
      const password = await resolvePlaintextPassword(dekOverrides);
      const elapsed = Date.now() - started;
      if (elapsed < MIN_PASSWORD_MODAL_LOAD_MS) {
        await delay(MIN_PASSWORD_MODAL_LOAD_MS - elapsed);
      }
      if (password == null) return;
      try {
        await navigator.clipboard.writeText(password);
        toast.success('Password copied', { position: 'top-right', autoClose: 2000 });
        try {
          await recordCredentialClientAction(id, { action: 'password_copied' });
          void fetchActivities();
        } catch {
          /* audit is best-effort */
        }
      } catch (e) {
        notifyError(e?.response?.data?.message || e?.message || 'Copy failed');
      }
    } catch (e) {
      const elapsedCatch = Date.now() - started;
      if (elapsedCatch < MIN_PASSWORD_MODAL_LOAD_MS) {
        await delay(MIN_PASSWORD_MODAL_LOAD_MS - elapsedCatch);
      }
      notifyError(e?.response?.data?.message || e?.message || 'Could not load password');
    } finally {
      passwordModalFlowLockRef.current = false;
      setPasswordRowBusy(false);
    }
  };

  const copyNotesFromModal = async () => {
    if (!notesModalText || notesModalText === '—' || !id) return;
    try {
      await navigator.clipboard.writeText(notesModalText);
      toast.success('Notes copied', { position: 'top-right', autoClose: 2000 });
    } catch (e) {
      notifyError(e?.response?.data?.message || e?.message || 'Copy failed');
    }
  };

  const copyPasswordFromModal = async () => {
    if (!passwordModalText || !id || passwordModalText === '—') return;
    try {
      await navigator.clipboard.writeText(passwordModalText);
      toast.success('Password copied', { position: 'top-right', autoClose: 2000 });
      try {
        await recordCredentialClientAction(id, { action: 'password_copied' });
        void fetchActivities();
      } catch {
        /* audit is best-effort */
      }
    } catch (e) {
      notifyError(e?.response?.data?.message || e?.message || 'Copy failed');
    }
  };

  const copyNotesAfterUnlock = async (dekOverrides) => {
    if (!id || !detail || !detail.has_notes || passwordModalFlowLockRef.current) return;
    passwordModalFlowLockRef.current = true;
    setPasswordRowBusy(true);
    const started = Date.now();
    try {
      const merged = await revealAndMergeSecrets(dekOverrides, { revealParts: 'notes' });
      const elapsed = Date.now() - started;
      if (elapsed < MIN_PASSWORD_MODAL_LOAD_MS) {
        await delay(MIN_PASSWORD_MODAL_LOAD_MS - elapsed);
      }
      if (merged == null) return;
      const n = merged.notes;
      if (n == null || String(n).trim() === '') {
        notifyError('No notes to copy');
        return;
      }
      try {
        await navigator.clipboard.writeText(String(n));
        toast.success('Notes copied', { position: 'top-right', autoClose: 2000 });
      } catch (e) {
        notifyError(e?.response?.data?.message || e?.message || 'Copy failed');
      }
    } catch (e) {
      const elapsedCatch = Date.now() - started;
      if (elapsedCatch < MIN_PASSWORD_MODAL_LOAD_MS) {
        await delay(MIN_PASSWORD_MODAL_LOAD_MS - elapsedCatch);
      }
      notifyError(e?.response?.data?.message || e?.message || 'Could not load notes');
    } finally {
      passwordModalFlowLockRef.current = false;
      setPasswordRowBusy(false);
    }
  };

  const copyAllCredentialDetails = async (dekOverrides) => {
    if (!id || !detail) return;
    setCopyAllLoading(true);
    try {
      const merged = await revealAndMergeSecrets(dekOverrides);
      if (merged == null || !merged.password) return;
      const text = buildCredentialClipboardText(
        merged.password,
        detail.has_notes ? merged.notes : ''
      );
      await navigator.clipboard.writeText(text);
      toast.success('Credential copied', { position: 'top-right', autoClose: 2000 });
      try {
        await recordCredentialClientAction(id, { action: 'credential_details_copied' });
        void fetchActivities();
      } catch {
        /* audit is best-effort */
      }
    } catch (e) {
      notifyError(e?.response?.data?.message || 'Copy failed');
    } finally {
      setCopyAllLoading(false);
    }
  };

  /** Always prompt corporate master password or personal vault password before reveal/copy (ZK + legacy server-side). */
  const startSecretGate = (action) => {
    if (!id || !detail || passwordRowBusy) return;
    if (action === 'copy_notes' && !detail.has_notes) return;
    if (action === 'view_notes' && !detail.has_notes) return;

    const usesPersonalCrypto = !!detail.private_uses_personal_dek;
    if (usesPersonalCrypto && !personalVaultInitialized) {
      toast.error('Create your personal vault before accessing this password.', {
        position: 'top-right',
      });
      return;
    }
    if (!usesPersonalCrypto && !vaultInitialized) {
      toast.error('Corporate Credentials Vault is not available.', { position: 'top-right' });
      return;
    }
    pendingSecretActionRef.current = action;
    setPasswordAccessUnlockOpen(true);
  };

  const handlePasswordAccessUnlockContinue = ({ dekBytes } = {}) => {
    const action = pendingSecretActionRef.current;
    pendingSecretActionRef.current = null;
    setPasswordAccessUnlockOpen(false);
    if (!detail) {
      notifyError('Could not continue. Try again.');
      return;
    }
    const pwdIv = detail.password_iv != null ? String(detail.password_iv).trim() : '';
    const notesIv = detail.notes_iv != null ? String(detail.notes_iv).trim() : '';
    const needsClientCrypto = !!(pwdIv || notesIv);

    if (needsClientCrypto && !dekBytes) {
      notifyError('Unlock did not return an encryption key. Try again.');
      return;
    }

    /* Modal-derived DEK must be passed through — context `dek` may not be updated until after re-render. */
    let overrides = {};
    if (dekBytes) {
      const usePersonal = !!detail.private_uses_personal_dek;
      overrides = usePersonal ? { personalDek: dekBytes } : { corporateDek: dekBytes };
    }

    if (action === 'view_password') void openPasswordModalFlow(overrides, 'password');
    else if (action === 'view_notes') void openPasswordModalFlow(overrides, 'notes');
    else if (action === 'copy') void copyPasswordAfterUnlock(overrides);
    else if (action === 'copy_all') void copyAllCredentialDetails(overrides);
    else if (action === 'copy_notes') void copyNotesAfterUnlock(overrides);
  };

  const handlePasswordAccessUnlockHide = () => {
    pendingSecretActionRef.current = null;
    setPasswordAccessUnlockOpen(false);
  };

  const openWebsite = () => {
    const href = toWebsiteHref(detail?.website);
    if (href) window.open(href, '_blank', 'noopener,noreferrer');
  };

  const goEdit = () => {
    if (!id || !detail) return;
    navigate(`/credential-vault/editCredential/${id}`, {
      state: credentialVaultListNavigateState(
        credentialVaultFormUiSide(detail),
        location.state?.credentialVaultListSearch
      ),
    });
  };

  const openDeletePasswordGate = () => {
    if (!id || !detail?.can_delete_credential) return;
    const usesPersonalCrypto = !!detail.private_uses_personal_dek;
    if (usesPersonalCrypto && !personalVaultInitialized) {
      toast.error('Create your personal vault before deleting credentials.');
      return;
    }
    if (!usesPersonalCrypto && !vaultInitialized) {
      toast.error('Corporate Credentials Vault is not available.');
      return;
    }
    setDeleteUnlockOpen(true);
  };

  const handleDeleteUnlockContinue = () => {
    setDeleteUnlockOpen(false);
    setShowDeleteModal(true);
  };

  const handleDeleteUnlockHide = () => {
    setDeleteUnlockOpen(false);
  };

  const confirmDelete = async () => {
    if (!id) return;
    setDeleteLoading(true);
    try {
      const res = await deleteCredential(id);
      if (res.data?.success) {
        toast.success('Deleted', { position: 'top-right', autoClose: 2000 });
        setShowDeleteModal(false);
        navigateBackToCredentialList();
      } else {
        notifyError('Delete failed');
      }
    } catch (e) {
      notifyError(e?.response?.data?.message || 'Delete failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  const websiteHref = detail ? toWebsiteHref(detail.website) : null;

  const credentialDetailToolbar = useMemo(() => (
    <>
      <div className="page-toolbar-bar__start">
        <button
          type="button"
          className="page-toolbar-back-btn"
          onClick={goList}
          title="Back"
          aria-label="Back"
        >
          <FiArrowLeft />
          Back
        </button>
      </div>
      {detail?.can_edit_credential || detail?.can_delete_credential ? (
        <div className="page-toolbar-bar__end">
          {detail.can_edit_credential ? (
            <button
              className="header-toolbar-btn seg-btn--wide"
              type="button"
              onClick={goEdit}
              title="Edit"
              aria-label="Edit"
            >
              <i className="fa fa-edit" aria-hidden />
              <span>Edit</span>
            </button>
          ) : null}
          {detail.can_delete_credential ? (
            <button
              className="header-toolbar-btn seg-btn--wide"
              type="button"
              onClick={openDeletePasswordGate}
              title="Delete"
              aria-label="Delete"
              style={{ color: '#dc3545' }}
            >
              <i className="fa fa-trash" aria-hidden />
              <span>Delete</span>
            </button>
          ) : null}
        </div>
      ) : null}
    </>
  ), [detail]);

  usePageToolbar(credentialDetailToolbar, [credentialDetailToolbar]);

  if (personalUnlockOpen) {
    return (
      <div className="credential-vault-detail-page">
        <ToastContainer />
        <CredentialVaultGateShell>
          <PersonalVaultUnlockAlert
            description={
              <p className="small text-muted mb-3">
                Enter your personal vault password.
                {' '}
                The personal vault locks automatically after {vaultIdleTimeoutMinutes} minute
                {vaultIdleTimeoutMinutes === 1 ? '' : 's'} of inactivity.
              </p>
            }
            password={personalUnlockPw}
            onPasswordChange={setPersonalUnlockPw}
            showPassword={showPersonalUnlockPw}
            onToggleShowPassword={() => setShowPersonalUnlockPw((v) => !v)}
            error={personalUnlockError}
            busy={personalUnlockBusy}
            onSubmit={(e) => {
              e.preventDefault();
              void confirmPersonalUnlockDetail();
            }}
          />
        </CredentialVaultGateShell>
      </div>
    );
  }

  return (
    <div className="credential-vault-detail-page">
      <ToastContainer />

      <Row className="g-4 credential-vault-detail-row">
        <Col lg={7} xl={7}>
          <Card className="user-list-card credential-vault-detail-main-card d-flex flex-column">
            <Card.Body className="credential-vault-detail-main-body flex-grow-1">
              {loading && (
                <div className="text-center py-5 credential-vault-detail-state">
                  <Spinner animation="border" role="status" />
                </div>
              )}
              {!loading && loadError && !detail && (
                <p className="text-danger mb-0 credential-vault-detail-state">{loadError}</p>
              )}
              {!loading && detail && (
                <>
                  <div className="credential-vault-detail-hero">
                    <div className="credential-vault-detail-hero-icon" aria-hidden>
                      <i className="fas fa-key" />
                    </div>
                    <div className="credential-vault-detail-hero-text">
                      <h1 className="credential-vault-detail-hero-title">{detail.title ?? '—'}</h1>
                      {detail.provider_name ? (
                        <p className="credential-vault-detail-hero-meta">{detail.provider_name}</p>
                      ) : null}
                    </div>
                    <div className="credential-vault-detail-hero-actions">
                      <button
                        type="button"
                        className="credential-vault-detail-icon-btn credential-vault-detail-icon-btn--hero"
                        onClick={() => startSecretGate('copy_all')}
                        disabled={copyAllLoading || passwordAccessUnlockOpen}
                        aria-label="Copy all fields: title, website, provider, user name, password, and notes if present"
                        title="Copy all fields"
                      >
                        {copyAllLoading ? (
                          <Spinner animation="border" size="sm" role="status" />
                        ) : (
                          <i className="fas fa-clone" aria-hidden />
                        )}
                      </button>
                    </div>
                  </div>

                  <dl className="credential-vault-detail-dl mb-0">
                    <div className="credential-vault-detail-pair">
                      <dt>Website</dt>
                      <dd>
                        <div className="credential-vault-website-row">
                          <span className="credential-vault-detail-value text-break">
                            {detail.website?.trim() ? detail.website : '—'}
                          </span>
                          {websiteHref ? (
                            <button
                              type="button"
                              className="credential-vault-detail-icon-btn"
                              onClick={openWebsite}
                              aria-label="Open website in new tab"
                              title="Open website"
                            >
                              <i className="fas fa-external-link-alt" aria-hidden />
                            </button>
                          ) : null}
                        </div>
                      </dd>
                    </div>
                    <div className="credential-vault-detail-pair">
                      <dt>Provider</dt>
                      <dd>{detail.provider_name || '—'}</dd>
                    </div>
                    <div className="credential-vault-detail-pair">
                      <dt>User name</dt>
                      <dd className="credential-vault-detail-mono">{detail.user_name ?? '—'}</dd>
                    </div>
                    <div className="credential-vault-detail-pair credential-vault-detail-pair--password">
                      <dt>Password</dt>
                      <dd>
                        <div className="credential-vault-password-block credential-vault-password-block--actions credential-vault-detail-secret-row">
                          <div className="credential-vault-password-action-btns credential-vault-password-action-btns--lead">
                            <button
                              type="button"
                              className="credential-vault-detail-icon-btn credential-vault-detail-icon-btn--password-action"
                              onClick={() => startSecretGate('view_password')}
                              disabled={passwordRowBusy || passwordAccessUnlockOpen}
                              aria-label="Show password"
                              title="Show password"
                            >
                              <i className="fas fa-eye" aria-hidden />
                            </button>
                            <button
                              type="button"
                              className="credential-vault-detail-icon-btn credential-vault-detail-icon-btn--password-action"
                              onClick={() => startSecretGate('copy')}
                              disabled={passwordRowBusy || passwordAccessUnlockOpen}
                              aria-label="Copy password"
                              title="Copy password"
                            >
                              <i className="fas fa-copy" aria-hidden />
                            </button>
                          </div>
                        </div>
                      </dd>
                    </div>
                    <div className="credential-vault-detail-pair credential-vault-detail-pair--password">
                      <dt>Notes</dt>
                      <dd>
                        {detail.has_notes ? (
                          <div className="credential-vault-password-block credential-vault-password-block--actions credential-vault-detail-secret-row">
                            <div className="credential-vault-password-action-btns credential-vault-password-action-btns--lead">
                              <button
                                type="button"
                                className="credential-vault-detail-icon-btn credential-vault-detail-icon-btn--password-action"
                                onClick={() => startSecretGate('view_notes')}
                                disabled={passwordRowBusy || passwordAccessUnlockOpen}
                                aria-label="Show notes"
                                title="Show notes"
                              >
                                <i className="fas fa-eye" aria-hidden />
                              </button>
                              <button
                                type="button"
                                className="credential-vault-detail-icon-btn credential-vault-detail-icon-btn--password-action"
                                onClick={() => startSecretGate('copy_notes')}
                                disabled={passwordRowBusy || passwordAccessUnlockOpen}
                                aria-label="Copy notes"
                                title="Copy notes"
                              >
                                <i className="fas fa-copy" aria-hidden />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </dd>
                    </div>
                    <div className="credential-vault-detail-pair">
                      <dt>Access scope</dt>
                      <dd>{summarizeAccess(detail)}</dd>
                    </div>
                    <div className="credential-vault-detail-pair">
                      <dt>Your permission</dt>
                      <dd>{formatMyAccessLevel(detail.my_access_level)}</dd>
                    </div>
                    <div className="credential-vault-detail-pair">
                      <dt>Created</dt>
                      <dd>{formatDetailTimestamp(detail.created_at)}</dd>
                    </div>
                    <div className="credential-vault-detail-pair credential-vault-detail-pair--last">
                      <dt>Last updated</dt>
                      <dd>{formatDetailTimestamp(detail.updated_at)}</dd>
                    </div>
                  </dl>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={5} xl={5}>
          <Card className="user-list-card credential-vault-log-card h-100">
            <Card.Header className="credential-vault-log-card-header">
              <Card.Title className="mb-0 credential-vault-log-card-title">Activity</Card.Title>
              <span className="credential-vault-log-card-hint">Recent changes</span>
            </Card.Header>
            <Card.Body className="credential-vault-log-body">
              {activitiesLoading && (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" role="status" />
                </div>
              )}
              {!activitiesLoading && activities.length === 0 && (
                <p className="text-muted small mb-0">No activity recorded yet.</p>
              )}
              {!activitiesLoading && activities.length > 0 && (
                <ul className="credential-vault-activity-list list-unstyled mb-0">
                  {activities.map((row) => (
                    <li key={row.id} className="credential-vault-activity-item">
                      <div className="credential-vault-activity-title">{formatActivityAction(row)}</div>
                      <div className="credential-vault-activity-meta text-muted small">
                        {row.performed_by_name || `User #${row.performed_by}`}
                        {row.created_at
                          ? ` · ${new Date(row.created_at).toLocaleString()}`
                          : ''}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <CredentialVaultNavUnlockModal
        show={deleteUnlockOpen && !!detail}
        side={detail?.private_uses_personal_dek ? 'personal' : 'corporate'}
        purpose="delete"
        onHide={handleDeleteUnlockHide}
        onContinue={handleDeleteUnlockContinue}
      />

      <CredentialVaultNavUnlockModal
        show={passwordAccessUnlockOpen && !!detail}
        side={detail?.private_uses_personal_dek ? 'personal' : 'corporate'}
        purpose="reveal"
        onHide={handlePasswordAccessUnlockHide}
        onContinue={handlePasswordAccessUnlockContinue}
      />

      <Modal
        show={passwordModalOpen}
        onHide={() => !passwordModalLoading && closePasswordModal()}
        backdrop={passwordModalLoading ? 'static' : true}
        keyboard={!passwordModalLoading}
        className="credential-vault-nav-unlock-modal-root"
        dialogClassName="credential-vault-nav-unlock-modal"
        contentClassName="credential-vault-nav-unlock-modal__content"
        style={passwordRevealModalStyle}
        onEntered={() => {
          requestAnimationFrame(() => remeasurePasswordRevealShift());
        }}
      >
        <Modal.Body className="credential-vault-nav-unlock-modal__body">
          <Alert
            variant="light"
            className="credential-vault-full-page-alert credential-vault-full-page-alert--in-modal mb-0"
          >
            <Alert.Heading className="h5 mb-3">
              {revealModalSurface === 'notes' ? 'Notes' : 'Password'}
            </Alert.Heading>
            {passwordModalLoading ? (
              <div
                className="credential-vault-password-modal-loading d-flex flex-column align-items-center justify-content-center py-4"
                aria-live="polite"
                aria-busy="true"
              >
                <Spinner
                  animation="border"
                  role="status"
                  className="mb-3 credential-vault-password-modal-spinner"
                />
                <span className="text-muted small">
                  {revealModalSurface === 'notes' ? 'Loading notes…' : 'Loading password…'}
                </span>
              </div>
            ) : passwordModalError ? (
              <>
                <div className="text-danger small mb-3">{passwordModalError}</div>
                <div className="task-form-toolbar toolbar toolbar-bottom pt-2 credential-vault-nav-unlock-toolbar credential-vault-password-modal-toolbar">
                  <button
                    type="button"
                    className="btn btn-cancel task-form-toolbar-btn"
                    onClick={closePasswordModal}
                  >
                    Close
                  </button>
                </div>
              </>
            ) : (
              <>
                {revealModalSurface === 'password' ? (
                  <div className="mb-3">
                    <div className="text-muted small mb-1">Password</div>
                    <div className="credential-vault-password-modal-value-row">
                      <code className="credential-vault-password-modal-value">{passwordModalText}</code>
                      <button
                        type="button"
                        className="credential-vault-detail-icon-btn credential-vault-detail-icon-btn--password-action"
                        onClick={copyPasswordFromModal}
                        aria-label="Copy password"
                        title="Copy password"
                      >
                        <i className="fas fa-copy" aria-hidden />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mb-0">
                    <div className="text-muted small mb-1">Notes</div>
                    <div className="credential-vault-notes-modal-row align-items-start">
                      <pre className="credential-vault-notes-modal-value mb-0 flex-grow-1">
                        {notesModalText || '—'}
                      </pre>
                      <button
                        type="button"
                        className="credential-vault-detail-icon-btn credential-vault-detail-icon-btn--password-action flex-shrink-0"
                        onClick={() => void copyNotesFromModal()}
                        aria-label="Copy notes"
                        title="Copy notes"
                      >
                        <i className="fas fa-copy" aria-hidden />
                      </button>
                    </div>
                  </div>
                )}
                <div className="task-form-toolbar toolbar toolbar-bottom pt-2 credential-vault-nav-unlock-toolbar credential-vault-password-modal-toolbar">
                  <button
                    type="button"
                    className="new-user-btn credential-vault-gate-primary-btn"
                    onClick={closePasswordModal}
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </Alert>
        </Modal.Body>
      </Modal>

      <Modal show={showDeleteModal} onHide={() => !deleteLoading && setShowDeleteModal(false)} centered>
        <Modal.Header closeButton={!deleteLoading}>
          <Modal.Title>Delete Credential</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Remove <strong>{detail?.title}</strong>? This cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" disabled={deleteLoading} onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" disabled={deleteLoading} onClick={confirmDelete}>
            {deleteLoading ? 'Deleting…' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>

      <InternalFooter />
    </div>
  );
}

export default CredentialVaultDetail;
